import { useState, useCallback } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Loader2Icon, ArrowLeftIcon, RefreshCwIcon, WrenchIcon, ExternalLinkIcon, AlertTriangleIcon, PowerOffIcon } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import type { AppException } from '@/types/app-exceptions'

interface Project {
  id: number
  name: string
}

interface Deployment {
  id: number
  status: string
  deploy_type: string
  deploy_tier: string | null
  compute_tier: string | null
  git_ref: string | null
  error_log: string | null
  completed_phases: string[]
  ecs_private_ip: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

interface DevInstance {
  id: number
  name: string
  production_status: string
  django_cloudfront_distribution_id: string | null
  django_cloudfront_domain: string | null
  django_ecr_repository_uri: string | null
  django_ecs_web_service_arn: string | null
  django_ecs_worker_service_arn: string | null
  django_ecs_web_task_definition_arn: string | null
  django_ecs_worker_task_definition_arn: string | null
  django_ecs_task_role_arn: string | null
  django_ecs_private_ip: string | null
  django_frontend_url: string | null
  django_api_url: string | null
  django_database_name: string | null
  django_database_url: string | null
  django_deployed_at: string | null
  production_deployed_at: string | null
  production_error_log: string | null
  production_url: string | null
  project?: Project
}

interface HealthCheck {
  ecs_web?: { status: string; running?: number; desired?: number; events?: Array<{ at: string; message: string }> }
  ecs_worker?: { status: string; running?: number; desired?: number; events?: Array<{ at: string; message: string }> }
  dns?: { status: string; domain?: string; resolves_to?: string[]; message?: string }
  s3_policy?: { status: string; statement_id?: string; message?: string }
  cloudfront?: { status: string; distribution_id?: string; domain?: string; cf_status?: string; enabled?: boolean }
  ecr?: { status: string; repository_uri?: string; image_count?: number }
  checked_at?: string
}

interface Props {
  dev_instance: DevInstance
  deploy_tier: string | null
  compute_tier: string | null
  vanity_subdomain: string | null
  deployments: Deployment[]
  cloudwatch_log_group_url: string | null
  s3_media_bucket: string | null
  auth: {
    user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean }
  }
  flash?: { success?: string; error?: string }
}

type HealthStatus = 'healthy' | 'degraded' | 'missing' | 'error' | 'not_created' | 'scaled_down' | 'inactive' | 'disabled' | 'deploying' | 'exists' | 'unknown'

function getHealthBadgeVariant(status: string): "success" | "warning" | "destructive" | "secondary" | "info" {
  switch (status) {
    case 'healthy':
    case 'exists':
      return 'success'
    case 'degraded':
    case 'deploying':
      return 'warning'
    case 'missing':
    case 'error':
      return 'destructive'
    default:
      return 'secondary'
  }
}

function Show({ dev_instance, deploy_tier, compute_tier, vanity_subdomain, deployments, cloudwatch_log_group_url, s3_media_bucket }: Props) {
  const isFrontendOnly = deploy_tier === 'frontend_only' || (!deploy_tier && !dev_instance.django_ecs_web_service_arn)
  const hasWorkers = deploy_tier === 'full_stack_with_workers' || (!deploy_tier && !!dev_instance.django_ecs_worker_service_arn)
  const { t } = useTranslation('admin')
  const [health, setHealth] = useState<HealthCheck | null>(null)
  const [loadingHealth, setLoadingHealth] = useState(false)
  const [logs, setLogs] = useState<{ web: string[]; worker: string[] }>({ web: [], worker: [] })
  const [activeLogTab, setActiveLogTab] = useState<'web' | 'worker'>('web')
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [instanceExceptions, setInstanceExceptions] = useState<AppException[]>([])
  const [exceptionsLoaded, setExceptionsLoaded] = useState(false)
  const [loadingExceptions, setLoadingExceptions] = useState(false)
  const [exceptionsFilter, setExceptionsFilter] = useState<string>('unresolved')
  const [showUndeployDialog, setShowUndeployDialog] = useState(false)
  const [undeploying, setUndeploying] = useState(false)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true)
    try {
      const response = await fetch(`/admin/django_infra/${dev_instance.id}/health_check`)
      const data = await response.json()
      setHealth(data)
    } catch (err) {
      console.error('Health check failed:', err)
    } finally {
      setLoadingHealth(false)
    }
  }, [dev_instance.id])

  const fetchLogs = useCallback(async (serviceType: 'web' | 'worker') => {
    setLoadingLogs(true)
    try {
      const response = await fetch(`/admin/django_infra/${dev_instance.id}/logs?service_type=${serviceType}`)
      const data = await response.json()
      setLogs(prev => ({ ...prev, [serviceType]: data.lines || [] }))
    } catch (err) {
      console.error('Log fetch failed:', err)
    } finally {
      setLoadingLogs(false)
    }
  }, [dev_instance.id])

  const handleRestart = useCallback((serviceType: string) => {
    setRestarting(true)
    router.post(
      `/admin/django_infra/${dev_instance.id}/restart_service`,
      { service_type: serviceType },
      { onFinish: () => setRestarting(false) }
    )
  }, [dev_instance.id])

  const fetchInstanceExceptions = useCallback(async (status?: string) => {
    const filterStatus = status ?? exceptionsFilter
    setLoadingExceptions(true)
    try {
      const response = await fetch(`/admin/django_infra/${dev_instance.id}/instance_exceptions?status=${filterStatus}`)
      if (response.ok) {
        const data = await response.json()
        setInstanceExceptions(data.exceptions || [])
        setExceptionsLoaded(true)
      }
    } catch {
      // silently handle
    } finally {
      setLoadingExceptions(false)
    }
  }, [dev_instance.id, exceptionsFilter])

  const handleUndeploy = useCallback(() => {
    setUndeploying(true)
    router.post(
      `/admin/django_infra/${dev_instance.id}/undeploy`,
      {},
      {
        onFinish: () => {
          setUndeploying(false)
          setShowUndeployDialog(false)
        }
      }
    )
  }, [dev_instance.id])

  const handleResolveException = useCallback(async (exceptionId: number) => {
    await fetch(`/admin/django_infra/${dev_instance.id}/resolve_exception`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ exception_id: exceptionId }),
    })
    fetchInstanceExceptions()
  }, [dev_instance.id, csrfToken, fetchInstanceExceptions])

  const handleIgnoreException = useCallback(async (exceptionId: number) => {
    await fetch(`/admin/django_infra/${dev_instance.id}/ignore_exception`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ exception_id: exceptionId }),
    })
    fetchInstanceExceptions()
  }, [dev_instance.id, csrfToken, fetchInstanceExceptions])

  const getAwsConsoleUrl = (value: string, type: string): string | null => {
    const region = 'us-east-1'
    switch (type) {
      case 'cloudfront_distribution':
        return `https://${region}.console.aws.amazon.com/cloudfront/v4/home#/distributions/${value}`
      case 'ecs_service': {
        // arn:aws:ecs:region:account:service/cluster/service-name
        const parts = value.split('/')
        if (parts.length >= 3) return `https://${region}.console.aws.amazon.com/ecs/v2/clusters/${parts[1]}/services/${parts[2]}`
        return null
      }
      case 'ecs_task_definition': {
        // arn:aws:ecs:region:account:task-definition/name:revision
        const tdPart = value.split('task-definition/')[1]
        if (!tdPart) return null
        const [name, rev] = tdPart.split(':')
        return `https://${region}.console.aws.amazon.com/ecs/v2/task-definitions/${name}/${rev || '1'}`
      }
      case 'iam_role': {
        // arn:aws:iam::account:role/role-name
        const roleName = value.split('/').pop()
        return roleName ? `https://${region}.console.aws.amazon.com/iam/home#/roles/details/${roleName}` : null
      }
      case 'ecr': {
        // account.dkr.ecr.region.amazonaws.com/repo-name
        const match = value.match(/^(\d+)\.dkr\.ecr\.[^.]+\.amazonaws\.com\/(.+)$/)
        if (match) return `https://${region}.console.aws.amazon.com/ecr/repositories/private/${match[1]}/${match[2]}`
        return null
      }
      case 's3_bucket':
        return `https://s3.console.aws.amazon.com/s3/buckets/${value}?region=${region}`
      case 'url':
        return value.startsWith('http') ? value : `https://${value}`
      default:
        return null
    }
  }

  const ResourceRow = ({ label, value, linkType }: { label: string; value: string | null | undefined; linkType?: string }) => {
    const url = value && linkType ? getAwsConsoleUrl(value, linkType) : null
    return (
      <div className="flex items-start gap-3 py-1.5">
        <span className="text-muted-foreground text-xs w-40 shrink-0">{label}</span>
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="font-mono text-xs break-all text-blue-500 hover:underline inline-flex items-center gap-1">
            {value}
            <ExternalLinkIcon className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          <span className="font-mono text-xs break-all">{value || '-'}</span>
        )}
      </div>
    )
  }

  const getDeploymentStatusBadge = (status: string) => {
    const variants: Record<string, "success" | "warning" | "destructive" | "secondary" | "info"> = {
      deployed: 'success',
      building: 'info',
      deploying: 'info',
      pending: 'secondary',
      failed: 'destructive',
    }
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>
  }

  return (
    <AdminLayout>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('djangoInfra.title'), href: '/admin/django_infra' },
        { label: dev_instance.name }
      ]} />

      <div className="p-6 space-y-6">
        {/* Page Title */}
        {dev_instance.project && (
          <div>
            <h1 className="text-2xl font-bold text-foreground">{dev_instance.project.name}</h1>
            <p className="text-sm text-muted-foreground">{dev_instance.name}</p>
          </div>
        )}

        {/* Back + Actions Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/admin/django_infra">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('djangoInfra.actions.back')}
              </Link>
            </Button>
            {deploy_tier && (
              <Badge variant="secondary">
                {t(`djangoInfra.tiers.${deploy_tier}`, { defaultValue: deploy_tier })}
              </Badge>
            )}
            {compute_tier && (
              <Badge variant="outline">
                {compute_tier}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchHealth} disabled={loadingHealth}>
              {loadingHealth ? (
                <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCwIcon className="h-4 w-4 mr-2" />
              )}
              {t('djangoInfra.actions.checkHealth')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Resource Inventory */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('djangoInfra.show.resourceInventory')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('djangoInfra.show.cloudfront')}</h4>
                <ResourceRow label={t('djangoInfra.show.distributionId')} value={dev_instance.django_cloudfront_distribution_id} linkType="cloudfront_distribution" />
                <ResourceRow label={t('djangoInfra.show.cloudfrontDomain')} value={dev_instance.django_cloudfront_domain} linkType="url" />
                <ResourceRow label={t('djangoInfra.show.frontendUrl')} value={dev_instance.django_frontend_url} linkType="url" />
                {vanity_subdomain && (
                  <ResourceRow label={t('djangoInfra.show.vanityUrl')} value={`https://${vanity_subdomain}.cayu.app`} linkType="url" />
                )}
                <ResourceRow label={t('djangoInfra.show.apiUrl')} value={dev_instance.django_api_url} linkType="url" />
              </div>
              {!isFrontendOnly && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('djangoInfra.show.ecs')}</h4>
                  <ResourceRow label={t('djangoInfra.show.webServiceArn')} value={dev_instance.django_ecs_web_service_arn} linkType="ecs_service" />
                  {hasWorkers && (
                    <ResourceRow label={t('djangoInfra.show.workerServiceArn')} value={dev_instance.django_ecs_worker_service_arn} linkType="ecs_service" />
                  )}
                  <ResourceRow label={t('djangoInfra.show.webTaskDef')} value={dev_instance.django_ecs_web_task_definition_arn} linkType="ecs_task_definition" />
                  {hasWorkers && (
                    <ResourceRow label={t('djangoInfra.show.workerTaskDef')} value={dev_instance.django_ecs_worker_task_definition_arn} linkType="ecs_task_definition" />
                  )}
                  <ResourceRow label={t('djangoInfra.show.taskRoleArn')} value={dev_instance.django_ecs_task_role_arn} linkType="iam_role" />
                  <ResourceRow label={t('djangoInfra.show.privateIp')} value={dev_instance.django_ecs_private_ip} />
                </div>
              )}
              {!isFrontendOnly && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('djangoInfra.show.ecr')}</h4>
                  <ResourceRow label={t('djangoInfra.show.ecrUri')} value={dev_instance.django_ecr_repository_uri} linkType="ecr" />
                </div>
              )}
              {!isFrontendOnly && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('djangoInfra.show.s3Media')}</h4>
                  <ResourceRow label={t('djangoInfra.show.mediaBucket')} value={s3_media_bucket} linkType="s3_bucket" />
                </div>
              )}
              {!isFrontendOnly && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('djangoInfra.show.database')}</h4>
                  <ResourceRow label={t('djangoInfra.show.databaseName')} value={dev_instance.django_database_name} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Live Health + Actions */}
          <div className="space-y-6">
            {/* Live Health */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('djangoInfra.show.liveHealth')}</CardTitle>
              </CardHeader>
              <CardContent>
                {!health ? (
                  <p className="text-sm text-muted-foreground">
                    Click "{t('djangoInfra.actions.checkHealth')}" to run live health checks
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(['ecs_web', 'ecs_worker', 'dns', 's3_policy', 'cloudfront', 'ecr'] as const).map((resource) => {
                      const check = health[resource]
                      if (!check) return null
                      const status = check.status as HealthStatus
                      return (
                        <div key={resource} className="flex items-center justify-between py-1">
                          <span className="text-sm font-medium">{t(`djangoInfra.columns.${
                            resource === 'ecs_web' ? 'ecsWeb'
                            : resource === 'ecs_worker' ? 'ecsWorker'
                            : resource === 's3_policy' ? 's3Policy'
                            : resource === 'ecr' ? 'ecr'
                            : resource}`)}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant={getHealthBadgeVariant(status)}>
                              {t(`djangoInfra.health.${status}`, { defaultValue: status })}
                            </Badge>
                            {(resource === 'ecs_web' || resource === 'ecs_worker') && 'running' in check && (
                              <span className="text-xs text-muted-foreground">{check.running}/{check.desired}</span>
                            )}
                            {resource === 'ecr' && 'image_count' in check && (
                              <span className="text-xs text-muted-foreground">{check.image_count} images</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    {health.checked_at && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Checked at {new Date(health.checked_at).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('djangoInfra.show.actionsTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!isFrontendOnly && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestart('web')}
                      disabled={restarting}
                    >
                      <RefreshCwIcon className="h-3.5 w-3.5 mr-1.5" />
                      {t('djangoInfra.actions.restartWeb')}
                    </Button>
                    {hasWorkers && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestart('worker')}
                        disabled={restarting}
                      >
                        <RefreshCwIcon className="h-3.5 w-3.5 mr-1.5" />
                        {t('djangoInfra.actions.restartWorker')}
                      </Button>
                    )}
                    {hasWorkers && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestart('both')}
                        disabled={restarting}
                      >
                        <RefreshCwIcon className="h-3.5 w-3.5 mr-1.5" />
                        {t('djangoInfra.actions.restartBoth')}
                      </Button>
                    )}
                  </div>
                )}
                {isFrontendOnly && (
                  <p className="text-sm text-muted-foreground">{t('djangoInfra.show.frontendOnlyNoActions')}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => router.post(`/admin/django_infra/${dev_instance.id}/fix_dns`)}
                  >
                    <WrenchIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('djangoInfra.actions.fixDns')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => router.post(`/admin/django_infra/${dev_instance.id}/fix_s3_policy`)}
                  >
                    <WrenchIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('djangoInfra.actions.fixS3Policy')}
                  </Button>
                </div>
                {dev_instance.production_status === 'deployed' && (
                  <div className="pt-3 border-t">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowUndeployDialog(true)}
                      disabled={undeploying}
                    >
                      {undeploying ? (
                        <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <PowerOffIcon className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {t('djangoInfra.actions.undeploy')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CloudWatch Logs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{t('djangoInfra.logs.title')}</CardTitle>
                {cloudwatch_log_group_url && (
                  <a href={cloudwatch_log_group_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1 text-xs">
                    {t('djangoInfra.logs.openInAws')}
                    <ExternalLinkIcon className="h-3 w-3" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border">
                  <button
                    className={`px-3 py-1 text-xs ${activeLogTab === 'web' ? 'bg-muted font-medium' : ''}`}
                    onClick={() => setActiveLogTab('web')}
                  >
                    {t('djangoInfra.logs.web')}
                  </button>
                  <button
                    className={`px-3 py-1 text-xs border-l ${activeLogTab === 'worker' ? 'bg-muted font-medium' : ''}`}
                    onClick={() => setActiveLogTab('worker')}
                  >
                    {t('djangoInfra.logs.worker')}
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(activeLogTab)}
                  disabled={loadingLogs}
                >
                  {loadingLogs ? (
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  ) : (
                    t('djangoInfra.logs.fetchLogs')
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {logs[activeLogTab].length > 0 ? (
              <pre className="bg-muted p-3 rounded-md overflow-x-auto text-xs font-mono max-h-80 overflow-y-auto">
                {logs[activeLogTab].join('\n')}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">{t('djangoInfra.logs.noLogs')}</p>
            )}
          </CardContent>
        </Card>

        {/* App Exceptions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangleIcon className="h-4 w-4" />
              {t('djangoInfra.exceptions.title')}
              {instanceExceptions.length > 0 && (
                <Badge variant="destructive">{instanceExceptions.length}</Badge>
              )}
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => fetchInstanceExceptions()} disabled={loadingExceptions}>
              {loadingExceptions ? <Loader2Icon className="h-4 w-4 animate-spin" /> : t('djangoInfra.exceptions.actions.load')}
            </Button>
          </CardHeader>
          {exceptionsLoaded && (
            <CardContent>
              <div className="flex gap-2 mb-3">
                {(['unresolved', 'resolved', 'ignored'] as const).map(status => (
                  <Button
                    key={status}
                    size="sm"
                    variant={exceptionsFilter === status ? 'default' : 'outline'}
                    onClick={() => { setExceptionsFilter(status); fetchInstanceExceptions(status) }}
                  >
                    {t(`djangoInfra.exceptions.status.${status}`)}
                  </Button>
                ))}
              </div>
              {instanceExceptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('djangoInfra.exceptions.empty')}</p>
              ) : (
                <div className="space-y-2">
                  {instanceExceptions.slice(0, 10).map(exc => (
                    <div key={exc.id} className="flex items-start justify-between p-2 border rounded text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-destructive">{exc.exception_class}</div>
                        <div className="text-xs text-muted-foreground truncate">{exc.message}</div>
                        {exc.location && <code className="text-xs bg-muted px-1 rounded">{exc.location}</code>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <Badge variant="outline">{exc.occurrence_count}x</Badge>
                        {exc.status === 'unresolved' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleResolveException(exc.id)}>
                              {t('djangoInfra.exceptions.actions.resolve')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleIgnoreException(exc.id)}>
                              {t('djangoInfra.exceptions.actions.ignore')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Deployment History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('djangoInfra.show.deploymentHistory')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {deployments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">ID</th>
                      <th className="text-left p-3 font-medium">{t('djangoInfra.columns.status')}</th>
                      <th className="text-left p-3 font-medium">{t('djangoInfra.columns.deployTier')}</th>
                      <th className="text-left p-3 font-medium">Git Ref</th>
                      <th className="text-left p-3 font-medium">{t('djangoInfra.show.phases')}</th>
                      <th className="text-left p-3 font-medium">IP</th>
                      <th className="text-left p-3 font-medium">Started</th>
                      <th className="text-left p-3 font-medium">Completed</th>
                      <th className="text-left p-3 font-medium">{t('djangoInfra.show.errorLog')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map((deployment) => (
                      <tr key={deployment.id} className="border-b">
                        <td className="p-3 font-mono text-xs">#{deployment.id}</td>
                        <td className="p-3">{getDeploymentStatusBadge(deployment.status)}</td>
                        <td className="p-3 text-xs">
                          <div className="flex items-center gap-1">
                            {deployment.deploy_tier ? (
                              <Badge variant="secondary">{t(`djangoInfra.tiers.${deployment.deploy_tier}`, { defaultValue: deployment.deploy_tier })}</Badge>
                            ) : '-'}
                            {deployment.compute_tier && deployment.compute_tier !== 'hobby' && (
                              <Badge variant="outline" className="text-[10px]">{deployment.compute_tier}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 font-mono text-xs">{deployment.git_ref || '-'}</td>
                        <td className="p-3 text-xs">
                          {deployment.completed_phases?.length > 0
                            ? deployment.completed_phases.join(', ')
                            : '-'}
                        </td>
                        <td className="p-3 font-mono text-xs">{deployment.ecs_private_ip || '-'}</td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {deployment.started_at
                            ? new Date(deployment.started_at).toLocaleString()
                            : '-'}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {deployment.completed_at
                            ? new Date(deployment.completed_at).toLocaleString()
                            : '-'}
                        </td>
                        <td className="p-3 text-xs max-w-xs truncate text-red-600">
                          {deployment.error_log || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                {t('djangoInfra.show.noDeployments')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showUndeployDialog} onOpenChange={setShowUndeployDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('djangoInfra.undeploy.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('djangoInfra.undeploy.description', { name: dev_instance.name })}
              <span className="block mt-2 text-sm">
                {t('djangoInfra.undeploy.preserved')}
              </span>
              <span className="block mt-1 text-sm font-semibold text-destructive">
                {t('djangoInfra.undeploy.destroyed')}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('djangoInfra.undeploy.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleUndeploy}
              disabled={undeploying}
            >
              {undeploying ? t('djangoInfra.undeploy.undeploying') : t('djangoInfra.undeploy.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}

export default Show
